"use client";
import { useState } from "react";
import type { ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProtectedMutation } from "@/hooks/use-protected-mutation";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { DatabaseZap, Loader2, Plus } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { post } from "@/utils/api-handler";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { FormField } from "@/components/shared/form-fields";
import { Loader } from "@/components/ui/loader";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { TEXTAREA_MIN_HEIGHT } from "@/utils/global";

interface DataSourceFormData {
  name: string;
  description: string;
}

interface FormErrors {
  name?: string;
  description?: string;
  general?: string;
}

interface CreateDataSourceDialogProps {
  onCreated?: () => void;
  trigger?: ReactNode;
}

export const CreateDataSourceDialog = ({
  onCreated,
  trigger,
}: CreateDataSourceDialogProps) => {
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});

  const [formData, setFormData] = useState<DataSourceFormData>({
    name: "",
    description: "",
  });

  const { mutate, isPending } = useProtectedMutation({
    mutationFn: (data: DataSourceFormData) => {
      return post("/api/documents/datasources", data);
    },
    requiredPermissions: ["CREATE_DATA_SOURCE"],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["datasources"] });
      queryClient.invalidateQueries({ queryKey: ["datasources-with-groups"] });
      resetForm();
      setIsDialogOpen(false);
      onCreated?.();
      toast.success("Data source created successfully");
    },
    onError: (error: any) => {
      const newErrors: FormErrors = {};
      const fieldLabels: Record<keyof FormErrors, string> = {
        name: "Data Source Name",
        description: "Description",
        general: "General Error",
      };

      if (error.response?.data) {
        const errorData = error.response.data;

        if (Array.isArray(errorData.detail)) {
          errorData.detail.forEach((err: any) => {
            if (err.loc && err.msg) {
              const field = err.loc[1] as keyof FormErrors;
              const fieldLabel = fieldLabels[field] || field;
              newErrors[field] = err.msg;
              toast.error(fieldLabel, {
                description: err.msg,
              });
            }
          });
        } else {
          toast.error("Error while creating data source", {
            description: error.response.data.message,
          });
        }
      }

      setErrors(newErrors);
    },
  });

  const handleInputChange = (
    field: keyof DataSourceFormData,
    value: string
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = () => {
    mutate(formData);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
    });
    setErrors({});
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
    resetForm();
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button>
            <DatabaseZap className="h-4 w-4 mr-1" /> Create Data Source
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="w-full p-0 max-w-[1000px] [&>button:last-child]:hidden max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <VisuallyHidden>
            <DialogTitle>Create Data Source</DialogTitle>
          </VisuallyHidden>
        </DialogHeader>
        <div className="sticky top-0 z-50 bg-background border-b">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 px-4 sm:px-6 py-4">
            <div className="text-center sm:text-left">
              <h2 className="sm:text-xl font-semibold">Create Data Source</h2>
              <p className="text-sm text-muted-foreground">
                Setup a data source to manage your documents and files
              </p>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 w-full sm:w-auto">
              <Button
                variant="outline"
                onClick={handleCancel}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                className="bg-primary hover:bg-primary/90 w-full sm:w-auto"
                onClick={handleSubmit}
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    <span>Creating...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    <span>Create Data Source</span>
                  </>
                )}
              </Button>
            </div>
          </div>
          {isPending && <Loader />}
        </div>

        <div className="px-4 py-4 space-y-6 overflow-y-auto">
          <Card className="shadow-sm border-border/50">
            <CardHeader className="border-b px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base font-medium">
                    Data Source Information
                  </CardTitle>
                  <CardDescription>
                    Basic data source configuration
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              <FormField
                label="Data Source Name"
                description="Enter a descriptive name for this data source"
                error={errors.name}
              >
                <Input
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="Data Source Name"
                  className={errors.name ? "border-destructive" : ""}
                />
              </FormField>

              <FormField
                label="Description"
                description="Add details about the data source"
                error={errors.description}
              >
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Type your description here..."
                  className={`${TEXTAREA_MIN_HEIGHT} ${errors.description ? "border-destructive" : ""}`}
                />
                <p className="text-sm mt-1 text-muted-foreground">
                  Max. 500 Characters
                </p>
              </FormField>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
};

